import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProjectDescription = ({ 
  formData, 
  onFieldChange, 
  activeLanguage, 
  errors = {} 
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [wordCount, setWordCount] = useState(0);

  const getFieldValue = (fieldName) => {
    return formData?.[fieldName]?.[activeLanguage] || '';
  };

  const handleDescriptionChange = (value) => {
    onFieldChange('description', {
      ...formData?.description,
      [activeLanguage]: value
    });
    
    // Update word count
    const words = value?.trim()?.split(/\s+/)?.filter(word => word?.length > 0);
    setWordCount(words?.length);
  };

  const insertFormatting = (format) => {
    const textarea = document.getElementById('project-description');
    const start = textarea?.selectionStart;
    const end = textarea?.selectionEnd;
    const selectedText = textarea?.value?.substring(start, end);
    
    let formattedText = '';
    switch (format) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        break;
      case 'list':
        formattedText = `\n- ${selectedText}`;
        break;
      case 'link':
        formattedText = `[${selectedText}](URL)`;
        break;
      default:
        formattedText = selectedText;
    }
    
    const newValue = textarea?.value?.substring(0, start) + formattedText + textarea?.value?.substring(end);
    handleDescriptionChange(newValue);
    
    // Focus back to textarea
    setTimeout(() => {
      textarea?.focus();
      textarea?.setSelectionRange(start + formattedText?.length, start + formattedText?.length);
    }, 0);
  };

  const formatButtons = [
    { icon: 'Bold', action: 'bold', tooltip: 'نص عريض' },
    { icon: 'Italic', action: 'italic', tooltip: 'نص مائل' },
    { icon: 'List', action: 'list', tooltip: 'قائمة نقطية' },
    { icon: 'Link', action: 'link', tooltip: 'رابط' }
  ];

  return (
    <div className={`bg-card border border-border rounded-lg ${isFullscreen ? 'fixed inset-4 z-300' : 'p-6'}`}>
      {isFullscreen && (
        <div className="p-6 h-full flex flex-col">
          {/* Fullscreen Header */}
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-border">
            <div className="flex items-center space-x-3">
              <Icon name="FileText" size={20} className="text-primary" />
              <h3 className="text-lg font-semibold text-foreground font-arabic">
                وصف المشروع - وضع ملء الشاشة
              </h3>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsFullscreen(false)}
              iconName="Minimize2"
              iconSize={18}
            />
          </div>
        </div>
      )}
      {!isFullscreen && (
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={18} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground font-arabic">
                وصف المشروع
              </h3>
              <p className="text-sm text-muted-foreground font-caption">
                اكتب وصفاً مفصلاً عن المشروع وأهدافه
              </p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsFullscreen(true)}
            iconName="Maximize2"
            iconSize={18}
            title="وضع ملء الشاشة"
          />
        </div>
      )}
      <div className={isFullscreen ? 'flex-1 flex flex-col' : ''}>
        {/* Formatting Toolbar */}
        <div className="flex items-center space-x-2 mb-4 p-3 bg-muted/50 rounded-lg border border-border">
          <div className="flex items-center space-x-1">
            {formatButtons?.map((button) => (
              <Button
                key={button?.action}
                variant="ghost"
                size="icon"
                onClick={() => insertFormatting(button?.action)}
                iconName={button?.icon}
                iconSize={16}
                title={button?.tooltip}
                className="w-8 h-8"
              />
            ))}
          </div>
          
          <div className="w-px h-6 bg-border mx-2" />
          
          <div className="flex items-center space-x-2 text-xs text-muted-foreground">
            <Icon name="Type" size={14} />
            <span>Markdown مدعوم</span>
          </div>
        </div>

        {/* Description Textarea */}
        <div className={`relative ${isFullscreen ? 'flex-1' : ''}`}>
          <textarea
            id="project-description"
            className={`
              w-full p-4 border border-border rounded-lg bg-background text-foreground
              placeholder-muted-foreground resize-none focus:outline-none focus:ring-2 
              focus:ring-primary focus:border-transparent transition-all duration-200
              ${isFullscreen ? 'h-full min-h-96' : 'h-48'}
              ${activeLanguage === 'ar' ? 'text-right' : 'text-left'}
            `}
            placeholder={
              activeLanguage === 'ar' ?'اكتب وصفاً مفصلاً عن المشروع، أهدافه، والنتائج المتوقعة...\n\nيمكنك استخدام Markdown للتنسيق:\n**نص عريض**\n*نص مائل*\n- قائمة نقطية'
                : activeLanguage === 'en' ?'Write a detailed description of the project, its objectives, and expected outcomes...\n\nYou can use Markdown for formatting:\n**Bold text**\n*Italic text*\n- Bullet list' :'Projenin ayrıntılı açıklamasını, hedeflerini ve beklenen sonuçlarını yazın...\n\nBiçimlendirme için Markdown kullanabilirsiniz:\n**Kalın metin**\n*İtalik metin*\n- Madde listesi'
            }
            value={getFieldValue('description')}
            onChange={(e) => handleDescriptionChange(e?.target?.value)}
            dir={activeLanguage === 'ar' ? 'rtl' : 'ltr'}
          />
          
          {errors?.description && (
            <p className="text-sm text-error mt-2 flex items-center space-x-2">
              <Icon name="AlertCircle" size={14} />
              <span>{errors?.description}</span>
            </p>
          )}
        </div>

        {/* Footer Stats */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border text-xs text-muted-foreground">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Icon name="Type" size={14} />
              <span>{getFieldValue('description')?.length} حرف</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <Icon name="Hash" size={14} />
              <span>{wordCount} كلمة</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={14} />
              <span>وقت القراءة: {Math.ceil(wordCount / 200)} دقيقة</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${getFieldValue('description')?.length > 0 ? 'bg-success' : 'bg-muted'}`} />
            <span>
              {getFieldValue('description')?.length > 0 ? 'تم الحفظ' : 'لم يتم الحفظ'}
            </span>
          </div>
        </div>
      </div>
      {/* Fullscreen Backdrop */}
      {isFullscreen && (
        <div 
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-200"
          onClick={() => setIsFullscreen(false)}
        />
      )}
    </div>
  );
};

export default ProjectDescription;